import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlayerBack, PlayerForward, PlayerPause, PlayerPlay } from '../../../assets';
import { apiService, SpeechSegment } from '../../../services/api.service';
import {
  formatSecondsShort,
  formatSpeakerShortName,
  getSegmentEndSec,
  getSegmentStartSec,
  getSpeakerName,
  PLAYBACK_SPEEDS,
  speakerNamesMatch,
  stripSpeakerOrgSuffix,
} from '../utils/audio-segments';
import { roundMinutes } from '../utils/voice-analytics.utils';
import styles from './meeting-audio-player.module.scss';

const WAVEFORM_BARS = 140;

type SpeakerTimeline = {
  id: number;
  name: string;
  color: string;
};

type Props = {
  audioUrl: string;
  /** Задача H1: если передан — доступен переключатель «Сжатая версия» (аудио без длинных пауз). */
  meetingId?: string;
  segments: SpeechSegment[];
  speakerTimelines: SpeakerTimeline[];
  durationSec: number;
  soloSpeakerName?: string | null;
  onExitSolo?: () => void;
  onTimeChange: (time: number) => void;
  onDurationChange: (duration: number) => void;
  seekRef?: React.MutableRefObject<((time: number) => void) | null>;
  togglePlayPauseRef?: React.MutableRefObject<(() => void) | null>;
};

function generateSyntheticPeaks(count: number): number[] {
  const peaks: number[] = [];
  let seed = 42;
  for (let i = 0; i < count; i++) {
    seed = (seed * 16807) % 2147483647;
    const noise = (seed % 1000) / 2500;
    const wave = Math.sin(i * 0.14) * 0.28 + Math.sin(i * 0.39) * 0.18 + Math.cos(i * 0.07) * 0.12;
    peaks.push(Math.min(1, Math.max(0.12, 0.38 + wave + noise)));
  }
  return peaks;
}

async function loadWaveformPeaks(url: string, barCount: number): Promise<number[]> {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('fetch failed');
    const arrayBuffer = await response.arrayBuffer();
    const audioContext = new AudioContext();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
    await audioContext.close();

    const channelData = audioBuffer.getChannelData(0);
    const blockSize = Math.max(1, Math.floor(channelData.length / barCount));
    const peaks: number[] = [];

    for (let i = 0; i < barCount; i++) {
      let max = 0;
      const start = i * blockSize;
      const end = Math.min(start + blockSize, channelData.length);
      for (let j = start; j < end; j++) {
        max = Math.max(max, Math.abs(channelData[j]));
      }
      peaks.push(max);
    }

    const peakMax = Math.max(...peaks, 0.001);
    return peaks.map(v => v / peakMax);
  } catch {
    return generateSyntheticPeaks(barCount);
  }
}

const MeetingAudioPlayer: React.FC<Props> = ({
  audioUrl,
  meetingId,
  segments,
  speakerTimelines,
  durationSec,
  soloSpeakerName = null,
  onExitSolo,
  onTimeChange,
  onDurationChange,
  seekRef,
  togglePlayPauseRef,
}) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(durationSec);
  const [speedIndex, setSpeedIndex] = useState(0);
  const [peaks, setPeaks] = useState<number[]>(() => generateSyntheticPeaks(WAVEFORM_BARS));

  // Задача H1: «Сжатая версия» — играет /audio/condensed вместо исходника. Длительности
  // ленивo грузятся по первому клику (первая генерация может занять десятки секунд).
  const [condensedActive, setCondensedActive] = useState(false);
  const [condensedLoading, setCondensedLoading] = useState(false);
  const [condensedInfo, setCondensedInfo] = useState<{ original_sec: number; condensed_sec: number } | null>(null);
  const [condensedError, setCondensedError] = useState('');

  const effectiveAudioUrl = condensedActive && meetingId
    ? apiService.getMeetingAudioCondensedUrl(meetingId)
    : audioUrl;

  const handleToggleCondensed = useCallback(async () => {
    if (!meetingId || condensedLoading) return;
    if (condensedActive) {
      setCondensedActive(false);
      return;
    }
    if (condensedInfo) {
      setCondensedActive(true);
      return;
    }
    setCondensedLoading(true);
    setCondensedError('');
    try {
      const info = await apiService.getMeetingAudioCondensedInfo(meetingId);
      setCondensedInfo(info);
      setCondensedActive(true);
    } catch (e) {
      setCondensedError(e instanceof Error ? e.message : t('voice-analytics.condensed-error'));
    } finally {
      setCondensedLoading(false);
    }
  }, [meetingId, condensedActive, condensedInfo, condensedLoading, t]);

  // Смена источника (обычное ↔ сжатое аудио) — таймкоды сегментов не совпадают со сжатым
  // треком, поэтому сбрасываем позицию воспроизведения, чтобы не оставлять «зависший» playhead.
  useEffect(() => {
    setCurrentTime(0);
    onTimeChange(0);
    setIsPlaying(false);
    audioRef.current?.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAudioUrl]);

  const effectiveDuration = audioDuration > 0 ? audioDuration : durationSec;

  const speakerColorByName = useMemo(() => {
    const map: Record<string, string> = {};
    speakerTimelines.forEach(s => { map[s.name] = s.color; });
    return map;
  }, [speakerTimelines]);

  const chronologicalSegments = useMemo(() => {
    return segments
      .map(seg => {
        const name = getSpeakerName(seg);
        return {
          startSec: getSegmentStartSec(seg),
          endSec: getSegmentEndSec(seg),
          color: speakerColorByName[name] || '#a3a3a3',
          name,
        };
      })
      .sort((a, b) => a.startSec - b.startSec);
  }, [segments, speakerColorByName]);

  const soloIntervals = useMemo(() => {
    if (!soloSpeakerName) return [];
    return chronologicalSegments
      .filter(s => speakerNamesMatch(s.name, soloSpeakerName))
      .map(s => ({ start: s.startSec, end: s.endSec }))
      .sort((a, b) => a.start - b.start);
  }, [chronologicalSegments, soloSpeakerName]);

  const skipToSoloRegion = useCallback((time: number): number | null => {
    if (!soloIntervals.length) return null;
    for (const iv of soloIntervals) {
      if (time < iv.start - 0.05) return iv.start;
      if (time >= iv.start - 0.05 && time < iv.end) return null;
    }
    return null;
  }, [soloIntervals]);

  const getColorAtTime = useCallback((time: number): string => {
    const seg = chronologicalSegments.find(
      s => time >= s.startSec && time < s.endSec,
    );
    return seg?.color || '#c8c8c8';
  }, [chronologicalSegments]);

  const currentSpeaker = useMemo(() => {
    const seg = chronologicalSegments.find(
      s => currentTime >= s.startSec && currentTime < s.endSec,
    );
    if (!seg) return null;
    return {
      name: formatSpeakerShortName(seg.name),
      fullName: stripSpeakerOrgSuffix(seg.name),
      color: seg.color,
    };
  }, [chronologicalSegments, currentTime]);

  const seek = useCallback((seconds: number, autoplay = true) => {
    const audio = audioRef.current;
    if (!audio || !effectiveDuration) return;
    let clamped = Math.max(0, Math.min(seconds, effectiveDuration));
    if (soloSpeakerName && soloIntervals.length) {
      const inSolo = soloIntervals.some(iv => clamped >= iv.start && clamped < iv.end);
      if (!inSolo) {
        const next = soloIntervals.find(iv => iv.start >= clamped) || soloIntervals[0];
        clamped = next.start;
      }
    }
    audio.currentTime = clamped;
    setCurrentTime(clamped);
    onTimeChange(clamped);
    if (autoplay) audio.play().catch(() => {});
  }, [effectiveDuration, onTimeChange, soloSpeakerName, soloIntervals]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, []);

  const skip = useCallback((delta: number) => {
    seek(currentTime + delta);
  }, [currentTime, seek]);

  const cycleSpeed = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const nextIndex = (speedIndex + 1) % PLAYBACK_SPEEDS.length;
    const speed = PLAYBACK_SPEEDS[nextIndex];
    audio.playbackRate = speed;
    setSpeedIndex(nextIndex);
  }, [speedIndex]);

  useEffect(() => {
    if (!effectiveAudioUrl) return;
    let cancelled = false;
    loadWaveformPeaks(effectiveAudioUrl, WAVEFORM_BARS).then(data => {
      if (!cancelled) setPeaks(data);
    });
    return () => { cancelled = true; };
  }, [effectiveAudioUrl]);

  useEffect(() => {
    if (!seekRef) return;
    seekRef.current = (time: number) => seek(time, true);
    return () => { seekRef.current = null; };
  }, [seekRef, seek]);

  useEffect(() => {
    if (!togglePlayPauseRef) return;
    togglePlayPauseRef.current = togglePlay;
    return () => { togglePlayPauseRef.current = null; };
  }, [togglePlayPauseRef, togglePlay]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || !peaks.length || !effectiveDuration) return;

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = wrap.clientWidth;
      const height = wrap.clientHeight;
      if (width <= 0 || height <= 0) return;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const barWidth = width / peaks.length;
      const centerY = height / 2;

      peaks.forEach((peak, i) => {
        const time = (i / peaks.length) * effectiveDuration;
        const barHeight = peak * (height * 0.88);
        const x = i * barWidth;
        const isPlayed = time <= currentTime;
        const color = getColorAtTime(time);

        ctx.fillStyle = isPlayed ? color : '#e3e3e3';
        const bx = x + 0.5;
        const by = centerY - barHeight / 2;
        const bw = Math.max(barWidth - 1, 1);
        const bh = barHeight;
        if (typeof ctx.roundRect === 'function') {
          ctx.beginPath();
          ctx.roundRect(bx, by, bw, bh, 1);
          ctx.fill();
        } else {
          ctx.fillRect(bx, by, bw, bh);
        }
      });

      const playheadX = (currentTime / effectiveDuration) * width;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
      ctx.fillRect(playheadX - 0.5, 0, 1, height);
    };

    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [peaks, currentTime, effectiveDuration, getColorAtTime]);

  const handleWaveformClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const wrap = wrapRef.current;
    if (!wrap || !effectiveDuration) return;
    const rect = wrap.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seek(ratio * effectiveDuration);
  };

  const playbackSpeed = PLAYBACK_SPEEDS[speedIndex];
  const speedLabel = playbackSpeed === 1 ? '1×' : `${playbackSpeed}×`;

  return (
    <div className={styles.player}>
      <audio
        ref={audioRef}
        src={effectiveAudioUrl}
        className={styles.hiddenAudio}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={(e) => {
          const audio = e.currentTarget;
          let nextTime = audio.currentTime;
          if (soloSpeakerName && soloIntervals.length) {
            const jump = skipToSoloRegion(nextTime);
            if (jump === null && soloIntervals.every(iv => nextTime >= iv.end)) {
              audio.pause();
              return;
            }
            if (jump !== null && Math.abs(jump - nextTime) > 0.1) {
              audio.currentTime = jump;
              nextTime = jump;
            }
          }
          setCurrentTime(nextTime);
          onTimeChange(nextTime);
        }}
        onSeeked={(e) => {
          const nextTime = e.currentTarget.currentTime;
          setCurrentTime(nextTime);
          onTimeChange(nextTime);
        }}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) {
            setAudioDuration(d);
            onDurationChange(d);
          }
        }}
      />

      {meetingId && (
        <div className={styles.condensedRow}>
          <button
            type="button"
            className={`${styles.condensedToggle} ${condensedActive ? styles.condensedToggleActive : ''}`}
            onClick={() => { void handleToggleCondensed(); }}
            disabled={condensedLoading}
            aria-pressed={condensedActive}
          >
            {condensedLoading
              ? t('voice-analytics.condensed-loading')
              : t('voice-analytics.condensed-toggle')}
          </button>
          {condensedInfo && (
            <span className={styles.condensedCaption}>
              {t('voice-analytics.condensed-caption', {
                original: roundMinutes(condensedInfo.original_sec),
                condensed: roundMinutes(condensedInfo.condensed_sec),
              })}
            </span>
          )}
          {condensedError && <span className={styles.condensedErrorText}>{condensedError}</span>}
        </div>
      )}

      {soloSpeakerName && (
        <div className={styles.soloBanner}>
          Только: {formatSpeakerShortName(soloSpeakerName)}
          <span className={styles.soloHint}> · паузы между репликами пропускаются</span>
          <button
            type="button"
            className={styles.soloExit}
            onClick={() => onExitSolo?.()}
            title="Слушать всю запись"
          >
            Вся запись
          </button>
        </div>
      )}

      {chronologicalSegments.length > 0 && (
        <div className={styles.segmentBar}>
          {chronologicalSegments.map((seg, i) => {
            const widthPct = ((seg.endSec - seg.startSec) / effectiveDuration) * 100;
            const isActive = currentTime >= seg.startSec && currentTime < seg.endSec;
            const playedPct = currentTime >= seg.endSec
              ? 100
              : currentTime <= seg.startSec
                ? 0
                : ((currentTime - seg.startSec) / (seg.endSec - seg.startSec)) * 100;

            return (
              <div
                key={`${seg.startSec}-${i}`}
                role="button"
                tabIndex={0}
                title={`${formatSecondsShort(seg.startSec)} → ${formatSecondsShort(seg.endSec)}`}
                className={`${styles.segmentBarItem} ${isActive ? styles.segmentBarItemActive : ''}`}
                style={{
                  width: `${Math.max(widthPct, 0.15)}%`,
                  backgroundColor: seg.color,
                }}
                onClick={() => seek(seg.startSec)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    seek(seg.startSec);
                  }
                }}
              >
                <div className={styles.segmentBarFill} style={{ width: `${playedPct}%` }} />
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.controls}>
        <button
          type="button"
          className={styles.btn}
          aria-label="Назад 10 секунд"
          onClick={() => skip(-10)}
        >
          <PlayerBack />
        </button>

        <button
          type="button"
          className={`${styles.btn} ${styles.btnPlay}`}
          aria-label={isPlaying ? 'Пауза' : 'Воспроизведение'}
          onClick={togglePlay}
        >
          {isPlaying ? <PlayerPause /> : <PlayerPlay />}
        </button>

        <button
          type="button"
          className={styles.btn}
          aria-label="Вперёд 10 секунд"
          onClick={() => skip(10)}
        >
          <PlayerForward />
        </button>

        <div
          ref={wrapRef}
          className={styles.waveformWrap}
          onClick={handleWaveformClick}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={effectiveDuration}
          aria-valuenow={currentTime}
          aria-label="Позиция воспроизведения"
        >
          <canvas ref={canvasRef} className={styles.waveform} />
        </div>

        <div className={styles.time}>
          <span className={styles.timeCurrent}>{formatSecondsShort(currentTime)}</span>
          <span className={styles.timeTotal}>{formatSecondsShort(effectiveDuration)}</span>
        </div>

        <button
          type="button"
          className={`${styles.btn} ${styles.btnSpeed}`}
          aria-label="Скорость воспроизведения"
          onClick={cycleSpeed}
        >
          {speedLabel}
        </button>
      </div>

      <div className={styles.nowSpeaking}>
        <span className={styles.nowSpeakingLabel}>Сейчас говорит:</span>
        {currentSpeaker ? (
          <div
            className={styles.nowSpeakingSpeaker}
            style={{ color: currentSpeaker.color }}
            title={currentSpeaker.fullName}
          >
            <span className={styles.nowSpeakingDot} style={{ backgroundColor: currentSpeaker.color }} />
            {currentSpeaker.name}
          </div>
        ) : (
          <span className={styles.nowSpeakingIdle}>—</span>
        )}
      </div>
    </div>
  );
};

export default MeetingAudioPlayer;
